// src/context/StatutoryWarningContext.js

import React, { createContext, useState, useEffect, useCallback } from "react";
import {
    collection,
    query,
    orderBy,
    startAfter,
    limit,
    getDocs,
    where,
} from "firebase/firestore";
import { db } from "../firebase";

export const StatutoryWarningContext = createContext();

// Define the number of items to show per page.
const PAGE_SIZE = 5;

export const StatutoryWarningProvider = ({ children }) => {
    const [statutoryWarnings, setStatutoryWarnings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [hasMore, setHasMore] = useState(true);
    const [lastVisible, setLastVisible] = useState(null);
    const [isSearching, setIsSearching] = useState(false);


    const addWarningToState = (newWarning) => {
        setStatutoryWarnings(prevWarnings => [newWarning, ...prevWarnings]);
    };

    const deleteWarningFromState = (warningId) => {
        setStatutoryWarnings(prevWarnings => prevWarnings.filter(sw => sw.id !== warningId));
    };
    /**
     * Fetches the initial set of statutory warnings.
     */
    const fetchInitialStatutoryWarnings = useCallback(async () => {
        setLoading(true);
        setIsSearching(false);
        try {
            // Query for statutory warnings
            const q = query(
                collection(db, "statutory-warnings"), // Changed collection
                orderBy("date", "desc"),
                limit(PAGE_SIZE + 1)
            );
            const documentSnapshots = await getDocs(q);

            const docs = documentSnapshots.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
            }));

            if (docs.length <= PAGE_SIZE) {
                setHasMore(false);
                setStatutoryWarnings(docs);
            } else {
                setHasMore(true);
                setLastVisible(documentSnapshots.docs[PAGE_SIZE - 1]);
                setStatutoryWarnings(docs.slice(0, PAGE_SIZE));
            }
        } catch (error) {
            console.error("Error fetching initial statutory warnings:", error); // Changed log
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchInitialStatutoryWarnings();
    }, [fetchInitialStatutoryWarnings]);

    /**
     * Fetches the next page of statutory warnings.
     */
    const fetchNextPage = async () => {
        if (loading || !hasMore) return 0;

        setLoading(true);
        try {
            const q = query(
                collection(db, "statutory-warnings"), // Changed collection
                orderBy("date", "desc"),
                startAfter(lastVisible),
                limit(PAGE_SIZE + 1)
            );
            const documentSnapshots = await getDocs(q);

            const newDocs = documentSnapshots.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
            }));

            if (newDocs.length <= PAGE_SIZE) {
                setHasMore(false);
            } else {
                setLastVisible(documentSnapshots.docs[PAGE_SIZE - 1]);
            }

            const docsToAdd = newDocs.slice(0, PAGE_SIZE);
            setStatutoryWarnings((prev) => [...prev, ...docsToAdd]);

            setLoading(false);
            return docsToAdd.length;
        } catch (error) {
            console.error("Error fetching next page of statutory warnings:", error); // Changed log
            setLoading(false);
            return 0;
        }
    };

    /**
     * Searches statutory warnings by title.
     */
    const searchStatutoryWarnings = async (searchTerm) => {
        if (!searchTerm.trim()) {
            fetchInitialStatutoryWarnings();
            return;
        }

        setLoading(true);
        setIsSearching(true);
        setHasMore(false);
        try {
            const q = query(
                collection(db, "statutory-warnings"), // Changed collection
                orderBy("title"),
                where("title", ">=", searchTerm),
                where("title", "<=", searchTerm + "\uf8ff")
            );

            const querySnapshot = await getDocs(q);
            const searchResults = querySnapshot.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
            }));
            setStatutoryWarnings(searchResults);
        } catch (error) {
            console.error("Error searching statutory warnings:", error); // Changed log
        } finally {
            setLoading(false);
        }
    };

    const value = {
        statutoryWarnings,
        setStatutoryWarnings,
        loading,
        hasMore,
        fetchNextPage,
        searchStatutoryWarnings,
        isSearching,
        addWarningToState,
        deleteWarningFromState
    };

    return <StatutoryWarningContext.Provider value={value}>{children}</StatutoryWarningContext.Provider>;
};